import { LitElement, html, css, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { effect } from "@preact/signals-core";

import tokensCss from "./styles/tokens.css?inline";
import "./components/editor-topbar.js";
import "./components/layer-panel.js";
import "./components/toast.js";
import "./components/status-bar.js";
import "./components/context-menu.js";
import "./components/modal.js";
import "./canvas/canvas-view.js";

import * as store from "./state/store.js";
import { SignalWatcher } from "./state/signal-watcher.js";
import type { DxfUnit, Theme } from "./state/types.js";
import { setHost, loadDxfFromString, exportAndDownload, closeDxf } from "./ops/io.js";
import { closeContextMenu, toggleSidebar } from "./ops/ui.js";
import { applyUnitSettings } from "./ops/units.js";
import { loadPreferences, savePreferences } from "./ops/prefs.js";

// The public web component. Everything the host (Rack + Stimulus) touches goes
// through this element:
//
//   <dxf-editor data-theme="dark"></dxf-editor>
//
// Public reflected attributes:
//   data-theme  "dark" | "light"
//
// Custom events (bubbling, composed):
//   dxf:load    — a DXF was parsed and loaded (detail: filename, entityCount)
//   dxf:change  — the model was mutated (detail: action)
//   dxf:export  — user exported the DXF (detail: filename, size)
//   dxf:close   — the loaded DXF was cleared
//
// Imperative API — call methods on the element:
//   load(content: string, filename?: string)   replace the loaded document
//   exportDxf(filename?: string)               trigger a download
//   close()                                    clear the loaded document
//   setUnits(units: Partial<UnitSettings>)     override auto-detected units
@customElement("dxf-editor")
export class DxfEditor extends SignalWatcher(LitElement) {
  static override styles = [
    unsafeCSS(tokensCss),
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        width: 100%;
        overflow: hidden;
        background: var(--bg-primary);
        color: var(--text-primary);
        position: relative;
        /* Set font-family here so every nested shadow root inherits it
           unless it explicitly overrides. Without this the browser's UA
           default (Times New Roman on Windows) leaks into components that
           don't set their own font-family. */
        font-family: var(--font-body);
      }

      :host([hidden]) { display: none; }

      .layout {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        min-height: 0;
      }

      .layout.reverse { flex-direction: row-reverse; }

      layer-panel { display: block; }
      layer-panel.collapsed { display: none; }

      .canvas-wrap {
        flex: 1 1 auto;
        position: relative;
        display: flex;
        min-width: 0;
        min-height: 0;
      }

      canvas-view { flex: 1 1 auto; min-height: 0; }

      /* Bottom-sheet backdrop. Only shown on mobile when the layer sheet
         is open — tap dismisses (invokes toggleSidebar). Sits between the
         canvas (z-auto) and the sheet (z:40 in layer-panel.ts) so a tap
         outside the sheet reads as "close it". */
      .sheet-scrim {
        position: absolute;
        inset: 0;
        background: var(--scrim, rgba(0, 0, 0, 0.5));
        z-index: 30;
        animation: scrim-in 220ms ease-out;
      }
      @keyframes scrim-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
    `,
  ];

  @property({ type: String, attribute: "data-theme", reflect: true })
  theme: Theme = "dark";

  private disposeEffects: Array<() => void> = [];
  private resizeObserver: ResizeObserver | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    setHost(this);
    store.write.theme(this.theme);
    // Publish host width into the store so components / ops can react to the
    // mobile breakpoint the same way CSS @media does. Seed synchronously so
    // first paint already knows whether we're in mobile mode.
    store.write.hostWidth(this.clientWidth || window.innerWidth);
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        if (w > 0) store.write.hostWidth(w);
      }
    });
    this.resizeObserver.observe(this);

    // Lazy-load the 3D view module the first time the user flips into 3D
    // mode. Bundles Three.js + earcut into a separate chunk (loaded from
    // esm.sh) so 2D-only sessions pay nothing for the 3D subsystem.
    let view3dLoaded = false;
    this.disposeEffects.push(
      effect(() => {
        if (store.viewMode() === "3d" && !view3dLoaded) {
          view3dLoaded = true;
          import("./components/view-3d.js").catch((err) => {
            console.error("[dxf-editor] failed to load 3D view", err);
          });
        }
      })
    );

    // Rehydrate persisted preferences before wiring effects so the first
    // paint uses the restored theme / grid / units instead of the defaults.
    loadPreferences();
    // Sync the reflected attribute with whatever theme was restored so the
    // <dxf-editor> element and its child shadow trees agree.
    this.theme = store.theme();
    this.setAttribute("data-theme", this.theme);

    // Reflect theme signal back to the attribute if ops change it programmatically.
    this.disposeEffects.push(
      effect(() => {
        const t = store.theme();
        if (this.theme !== t) {
          this.theme = t;
          this.setAttribute("data-theme", t);
        }
      })
    );

    // Persist any change to the preferences bag. First run is a synchronous
    // read of the current values which just re-writes what we loaded — cheap.
    this.disposeEffects.push(
      effect(() => {
        void store.theme();
        void store.sidebarPosition();
        void store.showGrid();
        void store.showGridRuler();
        void store.dxfUnit();
        void store.viewerUnit();
        void store.gridUnit();
        void store.gridSpacing();
        savePreferences();
      })
    );

    // Emit dxf:change on any model-mutating write. We watch the coarse union
    // of model signals and diff-detect by keeping the last serialised model
    // length in memory — cheap and good enough for host notifications.
    this.disposeEffects.push(
      effect(() => {
        void store.entities();
        void store.layers();
        void store.layerOrder();
        void store.selectedEntityIds();
        // Skip the initial synchronous read.
        queueMicrotask(() => {
          this.dispatchEvent(
            new CustomEvent("dxf:change", {
              detail: {
                entities: store.entities().length,
                layers: store.layerOrder().length,
                selected: store.selectedEntityIds().size,
              },
              bubbles: true,
              composed: true,
            })
          );
        });
      })
    );

    // Global click closes the context menu unless it was clicked *inside* the
    // menu (each menu item calls closeContextMenu directly).
    document.addEventListener("click", this.handleDocumentClick);

    // Suppress the browser's native right-click menu everywhere inside the
    // editor. Individual components still open our custom <context-menu>
    // through openContextMenu(). Bound in capture phase so the native menu
    // never surfaces regardless of which shadow child was targeted.
    this.addEventListener("contextmenu", this.handleContextMenu, true);

    // Native drag/drop of a .dxf file anywhere onto the element.
    this.addEventListener("dragover", this.handleDragOver);
    this.addEventListener("drop", this.handleDrop);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const d of this.disposeEffects) d();
    this.disposeEffects = [];
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    setHost(null);
    document.removeEventListener("click", this.handleDocumentClick);
    this.removeEventListener("contextmenu", this.handleContextMenu, true);
    this.removeEventListener("dragover", this.handleDragOver);
    this.removeEventListener("drop", this.handleDrop);
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has("theme")) store.write.theme(this.theme);
    // Reflect the mobile flag onto the host so CSS selectors like
    // :host([data-mobile]) can style children without JS coordination.
    const mobile = store.isMobile.value;
    if (mobile) this.setAttribute("data-mobile", "");
    else this.removeAttribute("data-mobile");
  }

  override render() {
    const position = store.sidebarPosition();
    const visible = store.sidebarVisible();
    const in3D = store.viewMode() === "3d";
    const mobile = store.isMobile.value;
    // Layer sidebar is 2D-only — the panel manages layer visibility/color,
    // which has no meaning against a re-extruded solid preview. On mobile
    // the panel renders itself as a bottom sheet layered OVER the canvas
    // instead of taking side space (see layer-panel :host(.sheet)), so we
    // slot it inside .canvas-wrap rather than as a flex sibling.
    const showSidebar = visible && !in3D;
    const sidebarClass = showSidebar ? "" : "collapsed";
    return html`
      <editor-topbar></editor-topbar>
      <div class="layout ${position === "right" ? "reverse" : ""} ${mobile ? "mobile" : ""}">
        ${mobile
          ? ""
          : html`<layer-panel
              class=${sidebarClass}
              position=${position}
            ></layer-panel>`}
        <div class="canvas-wrap">
          ${in3D ? html`<view-3d></view-3d>` : html`<canvas-view></canvas-view>`}
          <status-bar></status-bar>
          ${mobile && showSidebar
            ? html`<div
                class="sheet-scrim"
                @click=${() => toggleSidebar()}
              ></div>`
            : ""}
          ${mobile
            ? html`<layer-panel
                class="sheet ${sidebarClass}"
                position=${position}
              ></layer-panel>`
            : ""}
        </div>
      </div>
      <dxf-toast></dxf-toast>
      <context-menu></context-menu>
      <dxf-modal></dxf-modal>
    `;
  }

  private handleDocumentClick = (e: MouseEvent) => {
    // Composed path lets us see into the shadow DOM tree.
    const path = e.composedPath();
    for (const node of path) {
      if (node instanceof Element && node.tagName === "CONTEXT-MENU") return;
    }
    closeContextMenu();
  };

  private handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };

  private handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  private handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".dxf")) return;
    const content = await file.text();
    loadDxfFromString(content, file.name);
  };

  // ---- Imperative API ----

  load(content: string, filename?: string): void {
    loadDxfFromString(content, filename);
  }

  exportDxf(filename = "exported.dxf"): void {
    exportAndDownload(filename);
  }

  close(): void {
    closeDxf();
  }

  setUnits(units: {
    dxfUnit?: DxfUnit;
    viewerUnit?: DxfUnit;
    gridUnit?: DxfUnit;
    gridSpacing?: number;
  }): void {
    applyUnitSettings({
      dxfUnit: units.dxfUnit ?? store.dxfUnit(),
      viewerUnit: units.viewerUnit ?? store.viewerUnit(),
      gridUnit: units.gridUnit ?? store.gridUnit(),
      gridSpacing: units.gridSpacing ?? store.gridSpacing(),
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dxf-editor": DxfEditor;
  }
}
